// src/services/redux/api/endpoints/lifeInsurance/policyReportEndpoints.js
export const policyReportEndpoints = (builder) => ({
  // GET large resultset (report listing) - matches your controller route
  getPolicyReport: builder.query({
    query: (args = {}) => {
      // args: { pageNumber, pageSize, fromDate, toDate, paymentMode, policyStatus, groupIds, sortBy, searchValue }
      return {
        url: '/policy-management/generate-policy-report',
        method: 'GET',
        params: args,
      };
    },
    transformResponse: (response) => ({
      data: response?.data ?? [],
      totalCount: response?.totalCount ?? 0,
      success: !!response?.success,
      message: response?.message ?? '',
    }),
    providesTags: (result) => (result && Array.isArray(result.data)
      ? [...result.data.map((r, i) => ({ type: 'Policy', id: r.PolicyID ?? `${i}` })), { type: 'Policy', id: 'LIST' }]
      : [{ type: 'Policy', id: 'LIST' }]),
  }),

  // PREVIEW PDF (immediate generation) - returns JSON { success, pdf: "<base64>", isDummy }
  previewPolicyReport: builder.query({
    query: (params = {}) => ({
      url: '/policy-management/preview-policy-report',
      method: 'GET',
      params,
    }),
    transformResponse: (response) => response,
    // Preview is not a cacheable list of items; still provide a tag if you want to refetch on changes
    providesTags: (result, error, arg) => [{ type: 'Policy', id: 'PREVIEW' }],
  }),

  // CREATE export job (XLS / PDF) -> returns jobId. Use mutation (creates a job on backend).
  exportPolicyReport: builder.mutation({
    query: (params = {}) => ({
      url: '/policy-management/export-policy-report',
      method: 'GET', // your backend uses GET to create jobs (kept same)
      params,
    }),
    transformResponse: (response) => response,
    invalidatesTags: [{ type: 'Policy', id: 'LIST' }],
  }),

  // GET export job status
  getExportStatus: builder.query({
    query: (jobId) => ({
      url: `/policy-management/export/status/${jobId}`,
      method: 'GET',
    }),
    transformResponse: (response) => response,
    providesTags: (result, error, jobId) => [{ type: 'ExportJob', id: jobId }],
  }),

  // DOWNLOAD export file (returns blob)
  downloadExport: builder.query({
    query: (jobId) => ({
      url: `/policy-management/export/download/${jobId}`,
      method: 'GET',
      // instruct fetchBaseQuery to return blob so callers can create object URLs / save files
      responseHandler: 'blob',
    }),
    transformResponse: (response) => response,
    providesTags: (result, error, jobId) => [{ type: 'ExportDownload', id: jobId }],
  }),

  getPremiumDueReport: builder.query({
    query: (args = {}) => {
      // args: { pageNumber, pageSize, fromDate, toDate, paymentMode, premiumDueStatus, groupIds, sortBy, searchValue }
      return {
        url: '/policy-management/generate-premium-due-report',
        method: 'GET',
        params: args,
      };
    },
    transformResponse: (response) => ({
      data: response?.data ?? [],
      totalCount: response?.totalCount ?? 0,
      success: !!response?.success,
      message: response?.message ?? '',
    }),
    providesTags: (result) => (result && Array.isArray(result.data)
      ? [...result.data.map((r, i) => ({ type: 'PremiumDue', id: r.PremiumDueID ?? `${i}` })), { type: 'PremiumDue', id: 'LIST' }]
      : [{ type: 'PremiumDue', id: 'LIST' }]),
  }),

  // preview endpoint for premium due (immediate preview)
  previewPremiumDueReport: builder.query({
    query: (params = {}) => ({
      url: '/policy-management/preview-premium-due-report',
      method: 'GET',
      params,
    }),
    transformResponse: (response) => response,
    providesTags: (result, error, arg) => [{ type: 'PremiumDue', id: 'PREVIEW' }],
  }),

  exportPremiumDueReport: builder.mutation({
    query: (params = {}) => ({
      url: '/policy-management/export-premium-due-report',
      method: 'GET',
      params,
    }),
    transformResponse: (response) => response,
    invalidatesTags: [{ type: 'PremiumDue', id: 'LIST' }],
  }),

  getLapsedPoliciesReport: builder.query({
    query: (args = {}) => {
      // args: { pageNumber, pageSize, fromDate, toDate, paymentMode, premiumDueStatus, groupIds, sortBy, searchValue }
      return {
        url: '/policy-management/generate-lapsed-policies-report',
        method: 'GET',
        params: args,
      };
    },
    transformResponse: (response) => ({
      data: response?.data ?? [],
      totalCount: response?.totalCount ?? 0,
      success: !!response?.success,
      message: response?.message ?? '',
    }),
    providesTags: (result) => (result && Array.isArray(result.data)
      ? [...result.data.map((r, i) => ({ type: 'Lapsed', id: r.PolicyID ?? `${i}` })), { type: 'Lapsed', id: 'LIST' }]
      : [{ type: 'Lapsed', id: 'LIST' }]),
  }),

  getPremiumOutstandingReport: builder.query({
    query: (args = {}) => {
      return {
        url: '/policy-management/generate-premium-outstanding-report',
        method: 'GET',
        params: args,
      };
    },
    transformResponse: (response) => ({
      data: response?.data ?? [],
      totalCount: response?.totalCount ?? 0,
      success: !!response?.success,
      message: response?.message ?? '',
    }),
    providesTags: (result) => (result && Array.isArray(result.data)
      ? [...result.data.map((r, i) => ({ type: 'PremiumOutstanding', id: r.PolicyID ?? `${i}` })), { type: 'PremiumOutstanding', id: 'LIST' }]
      : [{ type: 'PremiumOutstanding', id: 'LIST' }]),
  }),

  // preview endpoint for premium outstanding (immediate preview)
  previewPremiumOutstandingReport: builder.query({
    query: (params = {}) => ({
      url: '/policy-management/preview-premium-outstanding-report',
      method: 'GET',
      params,
    }),
    transformResponse: (response) => response,
    providesTags: (result, error, arg) => [{ type: 'PremiumOutstanding', id: 'PREVIEW' }],
  }),

  exportPremiumOutstandingReport: builder.mutation({
    query: (params = {}) => ({
      url: '/policy-management/export-premium-outstanding-report',
      method: 'GET',
      params,
    }),
    transformResponse: (response) => response,
    invalidatesTags: [{ type: 'PremiumOutstanding', id: 'LIST' }],
  }),

  // preview endpoint for lapsed policies (immediate preview)
  previewLapsedPoliciesReport: builder.query({
    query: (params = {}) => ({
      url: '/policy-management/preview-lapsed-policies-report',
      method: 'GET',
      params,
    }),
    transformResponse: (response) => response,
    providesTags: (result, error, arg) => [{ type: 'Lapsed', id: 'PREVIEW' }],
  }),

  exportLapsedPoliciesReport: builder.mutation({
    query: (params = {}) => ({
      url: '/policy-management/export-lapsed-policies-report',
      method: 'GET',
      params,
    }),
    transformResponse: (response) => response,
    invalidatesTags: [{ type: 'Lapsed', id: 'LIST' }],
  }),

});


